import React from 'react';
import '../daily-fee.styles.scss';
import PageLayout from '../../../components/PageLayout';
import useSWR from 'swr';
import {
  formatCurrencyV2,
  formatDate,
  getItemById,
  getQueryString,
  getStatusColor,
  hasExpired,
  isAdmin,
} from '../../../utils/common.util';
import { useDebounceState } from '../../../utils/useDebounceState';
import Combobox from '../../../components/combobox/Combobox';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { Eye, Printer } from 'react-feather';
import { PrintView } from '../../../components/PrintView/print-view.component';
import PrintOutHead from '../../../components/PrintOutHead';
import { postMethod } from '../../../api/common';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import { ConfirmModal } from '../../../components/modal/confirm-modal/ConfirmModal.component';
import ReportsModal from './ReportsModal';
import { useUrlState } from '../../../utils/useUrlState';
import ExportReportButton from '../../../components/ExportReportButton';

const initialData = {
  dailyBillingFeeId: '',
  date: '',
  studentId: '',
  academicYearId: '',
  termId: '',
  classId: '',
  status: '',
};

export default function StudentBillsReportPage() {
  const [query, setQuery] = useUrlState(initialData);

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );

  const mainUrl = `daily-billing/list-bills`;
  const {
    data,
    isLoading: isLoadingPayments,
    error,
    mutate: refetchData,
  } = useSWR(
    query.date && query.dailyBillingFeeId && query.classId
      ? `${mainUrl}?${getQueryString(query)}`
      : null,
  );
  const billsData = data?.response ?? {};
  const isNotEmpty = billsData?.dailyBillingFee?.id;

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  const [isOpen, setIsOpen] = React.useState('');

  const {
    trigger,
    isMutating,
    error: voidError,
    reset,
  } = useCustomMutation(
    `daily-billing/bill/${billsData?.billingId}/void`,
    (url, { arg }) => postMethod(url, arg),
  );

  function handleClose() {
    setIsOpen('');
    reset();
  }

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: `Student Bills`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Student Bills Report</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
      isLoading={isLoadingPayments}
    >
      {/* FILTERS  */}
      <p className="mt-30 text-12">
        Select at least a date, fee type and a class to show report.
      </p>
      <div className="mt-10 card-row-4">
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                date: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ date: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Date"
            value={query.date}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ date: e.target.value })}
          />
        </DatePicker>
        <Select
          labelText="Fee Type"
          onChange={(e) => setQuery({ dailyBillingFeeId: e.target.value })}
          value={query.dailyBillingFeeId}
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <Select
          labelText="Class"
          onChange={(e) => setQuery({ classId: e.target.value })}
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({ academicYearId: e.target.value, termId: '' })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>

        <Combobox
          options={students ?? []}
          label="Select a Student"
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading} // Pass the loading state down
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />
        <Select
          labelText="Status"
          onChange={(e) => setQuery({ status: e.target.value, termId: '' })}
          value={query.status}
        >
          <SelectItem value="" text="All" />
          {['Generated', 'Voided']?.map((item) => (
            <SelectItem key={item} value={item} text={item} />
          ))}
        </Select>
      </div>

      <div className="mt-30">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}

        {!isNotEmpty && (
          <p className="text-14 mt-30 text-grey">
            No bills for the selected filters.
          </p>
        )}
        {isNotEmpty && (
          <>
            <PrintOutHead
              title={`Student Bills Report`}
              headings={[
                {
                  label: 'Bill Date',
                  value: formatDate(query.date),
                },
                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },
                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Class',
                  value:
                    getItemById(classes, query.classId)?.description ?? 'All',
                },
                {
                  label: 'Student',
                  value: query.studentId
                    ? students?.find((s) => s.id === query.studentId)?.fullName
                    : 'All',
                },
                {
                  label: 'Fee Type',
                  value:
                    getItemById(feeTypes, query.dailyBillingFeeId)?.name ??
                    'All',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <StudentBIllTable data={billsData} refetchData={refetchData} />
              </div>
            </PrintView>
          </>
        )}

        {isNotEmpty && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Daily Fee Student Bills Report.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
            {isAdmin() && !billsData?.dateVoided && (
              <CustomButton
                classes="danger"
                onClick={() => setIsOpen('void')}
                disabled={isMutating}
              >
                Void Entire Bill
              </CustomButton>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        title={`VOID ENTIRE BILL`}
        isOpen={isOpen === 'void'}
        closeModal={() => handleClose()}
        description={
          <>
            <p className="text-label">
              This action will void this entire bill and reverse all payments on
              it. This action cannot be undone.
            </p>
            <p className="font-bold mt-10">
              Are you sure you want to continue?
            </p>
          </>
        }
        actionText="VOID ENTIRE BILL"
        onClick={() =>
          trigger(null, {
            onSuccess: () => {
              refetchData();
              handleClose();
            },
          })
        }
        isLoading={isMutating}
        errorMessage={voidError ? voidError.message : null}
        actionBtnClasses="danger"
      />

      <ReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

function StudentBIllTable({ data, refetchData }) {
  const [isOpen, setIsOpen] = React.useState('');

  const {
    trigger,
    isMutating,
    error: reverseError,
    reset,
  } = useCustomMutation(
    `daily-billing/bill-item/${isOpen?.id}/void`,
    (url, { arg }) => postMethod(url, arg),
  );

  function handleClose() {
    setIsOpen('');
    reset();
  }
  return (
    <>
      <div className="mb-20">
        {reverseError && (
          <p className="text-14 mt-10 text-danger">{reverseError.message}</p>
        )}
        <p>{data.dailyBillingFee.name}</p>
        <table className="bx--data-table table-white">
          <thead>
            <tr>
              <th>Bill Date</th>
              <th>Student</th>
              <th>Class</th>
              <th>
                Amount Billed <br /> (GH₵)
              </th>
              <th>
                Amount Paid <br /> (GH₵)
              </th>
              <th>
                Balance Due <br /> (GH₵)
              </th>

              <th>Status</th>
              {isAdmin() && <th className="no-print">Action</th>}
            </tr>
          </thead>
          <tbody>
            {data?.studentBillItems?.map((entry, idx) => {
              const status = entry.dateVoided ? 'VOIDED' : 'GENERATED';
              entry.status = status;
              return (
                <tr key={entry.id}>
                  <td>{formatDate(entry.dateCreated)}</td>
                  <td>
                    {entry.student.firstName} {entry.student.lastName}
                  </td>
                  <td>{entry.student.classDescription}</td>
                  <td>{entry.amountBilled}</td>
                  <td>{entry.amountPaid}</td>
                  <td>{entry.balanceDue}</td>

                  <td className={getStatusColor(entry?.status)}>
                    {entry.status}
                  </td>
                  {isAdmin() && (
                    <td className="no-print">
                      <button
                        className="clean-btn text-primary"
                        onClick={() => setIsOpen(entry)}
                        disabled={isMutating}
                      >
                        View
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            <tr className="font-bold">
              <td colSpan="3">Total</td>
              <td>
                {data?.studentBillItems?.reduce(
                  (sum, entry) => sum + (Number(entry.amountBilled) || 0),
                  0,
                )}
              </td>
              <td>
                {data?.studentBillItems?.reduce(
                  (sum, entry) => sum + (Number(entry.amountPaid) || 0),
                  0,
                )}
              </td>
              <td>
                {data?.studentBillItems?.reduce(
                  (sum, entry) => sum + (Number(entry.balanceDue) || 0),
                  0,
                )}
              </td>
              <td colSpan="2"></td>
              {isAdmin() && <td className="no-print"></td>}
            </tr>
          </tbody>
        </table>
      </div>

      <ConfirmModal
        title={isOpen?.status === 'VOIDED' ? `VOIDED BILL` : `VOID THIS BILL`}
        isOpen={typeof isOpen === 'object'}
        closeModal={() => setIsOpen('')}
        description={
          <>
            {isOpen?.status === 'GENERATED' && (
              <>
                <p className="text-label">
                  This action will void this bill and reverse all payments on
                  it. This action cannot be undone.
                </p>
                <p className="font-bold mt-10">
                  Are you sure you want to continue?
                </p>
              </>
            )}
            <div>
              <div
                style={{
                  fontSize: '12px',
                  marginTop: '20px',
                  display: 'grid',
                  gap: '10px',
                  gridTemplateColumns: '150px 1fr',
                }}
              >
                <span className="font-bold">Student Name:</span>
                <span>
                  {isOpen?.student?.firstName} {isOpen?.student?.lastName}
                </span>
                <span className="font-bold">Unique ID:</span>
                <span>{isOpen?.student?.studentId}</span>
                <span className="font-bold">Fee Type:</span>
                <span>{isOpen?.feeDescription}</span>
                <span className="font-bold">Amount Billed (GH₵):</span>
                <span>{formatCurrencyV2(isOpen?.amountBilled)}</span>
                <span className="font-bold">Amount Paid (GH₵):</span>
                <span>{formatCurrencyV2(isOpen?.amountPaid)}</span>
                <span className="font-bold">Balance Due (GH₵):</span>
                <span>{formatCurrencyV2(isOpen?.balanceDue)}</span>
                <span className="font-bold">Bill Date:</span>
                <span>{formatDate(isOpen?.dateCreated)}</span>
                <span className="font-bold">Date Voided:</span>
                <span>{formatDate(isOpen?.dateVoided)}</span>
              </div>
            </div>
          </>
        }
        actionText="VOID BILL"
        onClick={
          isOpen?.status === 'VOIDED'
            ? undefined
            : () =>
                trigger(null, {
                  onSuccess: () => {
                    refetchData();
                    handleClose();
                  },
                })
        }
        isLoading={isMutating}
        errorMessage={reverseError ? reverseError.message : null}
        actionBtnClasses="danger"
      />
    </>
  );
}
